import { refreshStreamSnapshotAfterMutation } from "../refreshStreamSnapshot";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FolderTypeEnum,
  ILiveStream,
  ILiveStreamAnalytics,
  ILiveStreamScheduledShow,
  IOverlay,
  IVideo,
  LiveStreamEndOptionEnum,
  LiveStreamJingleEveryTypeEnum,
  LiveStreamOrientationEnum,
  LiveStreamPlaybackOrderEnum,
  LiveStreamResolutionEnum,
  LiveStreamStartOptionEnum,
  LiveStreamStatus,
  LiveStreamTypeEnum,
  OverlayTypeEnum,
} from "../../types/models";
import LiveStreamServiceInstance from "../../api/LiveStreamApi";
import {
  ICreatePlaylistRequest,
  ICreateVideoRequest,
  IOverlaySelectable,
  IScheduledShow,
  IStreamSelectedItem,
  IUpdateLiveStreamRequest,
  IUpdatePlaylistRequest,
  IUpdateVideoRequest,
} from "../../types/interfaces";
import type { StreamEntitlement } from "../../types/billing";
import {
  buildLiveStreamSettingsBody,
  findEmptyScheduledShow,
  precheckScheduledShows,
} from "../../helpers/liveStreamSettingsBody";
import { notification } from "antd";
import { dispatchErrorNotification } from "../../helpers/errorDispatch";
import { draftSourceFingerprint } from "../../helpers/draftSourceFingerprint";
import i18n from "../../i18n";
import { exceedsStreamTier } from "../../helpers/resolutionTier";
import {
  setSelectedBackgroundId,
  setSelectedItems,
  setSelectedJingleItems,
  setSelectedSecondaryItems,
  setShows,
} from "./MediaSlice";
import dayjs from "dayjs";
import {
  getJingleItemId,
  getSecondaryItemId,
  getShowItemId,
  parseJingleItemId,
  parseSecondaryItemId,
  parseShowItemId,
} from "../../helpers/helpers";
import {
  DEFAULT_AUDIO_BITRATE,
  DEFAULT_FPS,
  DEFAULT_VIDEO_BITRATE,
  ENABLE_PRO_PLAN,
} from "../../constants/constants";
import {
  CONTAINER_HEIGHT,
  CONTAINER_WIDTH,
} from "../../Components/OverlayEditor/utils";
import type { RootState } from "../store";

export interface LiveStreamForm {
  id?: string;
  backgroundId: string | null;
  title: string;
  streamType?: LiveStreamTypeEnum;
  playbackOrder: LiveStreamPlaybackOrderEnum;
  streamingPlatformsIds: string[];
  repeatOnEnd: boolean;
  termsAgreedAt: Date | null;
  contentAgreedAt: Date | null;
  startDate?: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
  startOption: LiveStreamStartOptionEnum;
  endOption: LiveStreamEndOptionEnum;
  overlays: IOverlaySelectable[];
  history: IOverlay[][];
  historyStep: number;
  orientation: LiveStreamOrientationEnum;
  videoWidth: number;
  videoHeight: number;
  videoLeft: number;
  videoTop: number;
  videoBitrate: number;
  videoFPS: number;
  resolution: LiveStreamResolutionEnum;
  fps: number;
  audioBitrate: number;
  jingleEvery: number;
  jingleEveryType: LiveStreamJingleEveryTypeEnum;
  jinglePlaybackOrder: LiveStreamPlaybackOrderEnum;
  secondaryVideoVolume: number;
  crfMode: boolean;
  videoFadeInDuration: number;
  videoFadeOutDuration: number;
  audioMixingEnabled: boolean;
  audioMixingDuration: number;
  isGST: boolean;
}

export interface LiveStreamWizardDraftSnapshot {
  liveStreamForm: LiveStreamForm;
  selectedItems: IStreamSelectedItem[];
  selectedJingleItems: IStreamSelectedItem[];
  selectedSecondaryItems: IStreamSelectedItem[];
  selectedBackgroundId: string | null;
  scheduledShows: IScheduledShow[];
}

interface LiveStreamWizardDraft {
  initialized: boolean;
  dirty: boolean;
  snapshot: LiveStreamWizardDraftSnapshot | null;
  /** Last known saved form and selections, retained when a draft is captured. */
  baseSnapshot?: LiveStreamWizardDraftSnapshot;
  baseContentRevision?: number;
  /** A51: draftSourceFingerprint of the server copy this draft is based on. */
  baseFingerprint?: string;
  /** A51: set while an edited draft is behind changed server data (banner shown). */
  staleServerRevision?: number;
  staleServerFingerprint?: string;
  /** Persisted targets from the server copy this draft was built from. */
  basePersistedTargets?: PersistedContentTargets;
  /** A referenced content row or show was removed while this draft was edited. */
  missingPersistedContent?: boolean;
}

interface PersistedContentTargets {
  playlistId?: string | null;
  videoId?: string | null;
  jinglePlaylistId?: string | null;
  jingleVideoId?: string | null;
  secondaryVideoId?: string | null;
  shows: { id: string; playlistId?: string; videoId?: string }[];
}

const persistedContentTargets = (stream: ILiveStream): PersistedContentTargets => ({
  playlistId: stream.playlistId,
  videoId: stream.videoId,
  jinglePlaylistId: stream.jinglePlaylistId,
  jingleVideoId: stream.jingleVideoId,
  secondaryVideoId: stream.secondaryVideoId,
  shows: (stream.liveStreamScheduledShows ?? []).map(({ id, playlistId, videoId }) => ({
    id,
    playlistId,
    videoId,
  })),
});

const draftTargetStatus = (
  base: PersistedContentTargets,
  server: ILiveStream,
  submittedShows: IScheduledShow[]
): "same" | "deleted" | "changed" => {
  let changed = false;
  const directFields = [
    "playlistId",
    "videoId",
    "jinglePlaylistId",
    "jingleVideoId",
    "secondaryVideoId",
  ] as const;
  for (const field of directFields) {
    const before = base[field] ?? null;
    const after = server[field] ?? null;
    if (before === after) continue;
    if (before && !after) return "deleted";
    changed = true;
  }
  const serverShows = new Map(
    (server.liveStreamScheduledShows ?? []).map((show) => [show.id, show])
  );
  for (const show of submittedShows) {
    if (show.id.startsWith("new")) continue;
    const expected = base.shows.find((saved) => saved.id === show.id);
    const actual = serverShows.get(show.id);
    if (!actual) return "deleted";
    if (expected &&
      (expected.playlistId !== actual.playlistId ||
        expected.videoId !== actual.videoId)) changed = true;
  }
  return changed ? "changed" : "same";
};

interface LiveStreamState {
  activeLiveStreams: ILiveStream[];
  activeLiveStreamsLoading: boolean;
  updateLiveStreamLoading: boolean;
  liveStreamByIdLoading: boolean;
  liveStreamById: ILiveStream | null;
  liveStreamByIdRequestedId: string | null;
  liveStreamByIdRequestId: string | null;
  liveStreamByIdError: string | null;
  /** HTTP status of the last failed detail load (0 without a response); null otherwise. 404 = the stream does not exist. */
  liveStreamByIdErrorStatus: number | null;
  liveStreamByIdAnalytics: ILiveStreamAnalytics[] | null;
  selectedPlatformId: string | null;
  liveStreamForm: LiveStreamForm;
  activeWizardDraftId: string | null;
  wizardDraftsByStream: Record<string, LiveStreamWizardDraft>;
}

const initialState: LiveStreamState = {
  activeLiveStreams: [],
  activeLiveStreamsLoading: false,
  updateLiveStreamLoading: false,
  liveStreamByIdLoading: false,
  liveStreamById: null,
  liveStreamByIdRequestedId: null,
  liveStreamByIdRequestId: null,
  liveStreamByIdError: null,
  liveStreamByIdErrorStatus: null,
  liveStreamByIdAnalytics: null,
  selectedPlatformId: null,
  activeWizardDraftId: null,
  wizardDraftsByStream: {},
  liveStreamForm: {
    title: "",
    backgroundId: "",
    playbackOrder: LiveStreamPlaybackOrderEnum.sequential,
    streamingPlatformsIds: [],
    repeatOnEnd: true,
    startOption: LiveStreamStartOptionEnum.now,
    endOption: LiveStreamEndOptionEnum.never,
    overlays: [],
    history: [],
    historyStep: 0,
    termsAgreedAt: null,
    contentAgreedAt: null,
    orientation: LiveStreamOrientationEnum.horizontal,
    videoWidth: CONTAINER_WIDTH,
    videoHeight: CONTAINER_HEIGHT,
    videoLeft: 0,
    videoTop: 0,
    videoBitrate: DEFAULT_VIDEO_BITRATE,
    audioBitrate: DEFAULT_AUDIO_BITRATE,
    videoFPS: DEFAULT_FPS,
    resolution: LiveStreamResolutionEnum.r1080p,
    fps: 30,
    jingleEvery: 5,
    jingleEveryType: LiveStreamJingleEveryTypeEnum.songs,
    jinglePlaybackOrder: LiveStreamPlaybackOrderEnum.sequential,
    secondaryVideoVolume: 20,
    crfMode: false,
    videoFadeInDuration: 3,
    videoFadeOutDuration: 2,
    audioMixingEnabled: false,
    audioMixingDuration: 3,

    // GST Settings
    isGST: true,
  },
};

interface LiveStreamByIdFailure {
  message: string;
  status: number;
}

const requestLiveStreamById = createAsyncThunk<
  ILiveStream,
  { id: string; silent: boolean },
  { rejectValue: LiveStreamByIdFailure }
>(
  "liveStream/getLiveStreamById",
  async ({ id, silent }, { dispatch, getState, requestId, rejectWithValue }) => {
    try {
      const { data: liveStream } =
        await LiveStreamServiceInstance.getLiveStreamById(id);
      const detail = (getState() as { liveStream: LiveStreamState }).liveStream;
      if (
        liveStream.id === id &&
        detail.liveStreamByIdRequestedId === id &&
        detail.liveStreamByIdRequestId === requestId
      ) {
        (dispatch as any)(activateLiveStreamWizardDraft({ liveStream }));
      }
      return liveStream;
    } catch (err: any) {
      const status = typeof err?.status === "number" ? err.status : 0;
      const detail = (getState() as { liveStream: LiveStreamState }).liveStream;
      // A missing stream gets its own "Stream not found" page; a toast would only repeat it.
      if (
        !silent &&
        status !== 404 &&
        detail.liveStreamByIdRequestedId === id &&
        detail.liveStreamByIdRequestId === requestId
      ) {
        dispatchErrorNotification(err);
      }
      return rejectWithValue({ message: err?.message ?? "Request failed", status });
    }
  }
);

export const liveStreamSlice = createSlice({
  name: "liveStream",
  initialState,
  reducers: {
    setSelectedPlatformId(state, action: PayloadAction<string | null>) {
      state.selectedPlatformId = action.payload;
    },
    setUpdateLiveStreamLoading(state, action: PayloadAction<boolean>) {
      state.updateLiveStreamLoading = action.payload;
    },
    setBackgroundId(state, action: PayloadAction<string>) {
      state.liveStreamForm.backgroundId = action.payload;
    },
    setActiveLiveStreams(state, action: PayloadAction<ILiveStream[]>) {
      state.activeLiveStreams = action.payload;
    },
    setActiveLiveStreamsLoading(state, action: PayloadAction<boolean>) {
      state.activeLiveStreamsLoading = action.payload;
    },
    setLiveStreamTitle(state, action: PayloadAction<string>) {
      state.liveStreamForm.title = action.payload;
    },
    setLiveStreamPlaybackOrder(
      state,
      action: PayloadAction<LiveStreamPlaybackOrderEnum>
    ) {
      state.liveStreamForm.playbackOrder = action.payload;
    },
    setLiveStreamJinglePlaybackOrder(
      state,
      action: PayloadAction<LiveStreamPlaybackOrderEnum>
    ) {
      state.liveStreamForm.jinglePlaybackOrder = action.payload;
    },
    setLiveStreamStreamingPlatformsIds(state, action: PayloadAction<string[]>) {
      state.liveStreamForm.streamingPlatformsIds = action.payload;
    },
    setLiveStreamRepeatOnEnd(state, action: PayloadAction<boolean>) {
      state.liveStreamForm.repeatOnEnd = action.payload;
    },
    setLiveStreamStartOption(
      state,
      action: PayloadAction<LiveStreamStartOptionEnum>
    ) {
      state.liveStreamForm.startOption = action.payload;
    },
    setLiveStreamEndOption(
      state,
      action: PayloadAction<LiveStreamEndOptionEnum>
    ) {
      state.liveStreamForm.endOption = action.payload;
    },
    setLiveStreamOverlays(state, action: PayloadAction<IOverlaySelectable[]>) {
      state.liveStreamForm.overlays = action.payload;
    },
    updateLiveStreamOverlay(state, action: PayloadAction<IOverlaySelectable>) {
      const updatedOverlay = action.payload;
      state.liveStreamForm.overlays = state.liveStreamForm.overlays.map(
        (overlay) =>
          overlay.id === updatedOverlay.id ? updatedOverlay : overlay
      );
      state.liveStreamForm.history.push([...state.liveStreamForm.overlays]);
      state.liveStreamForm.historyStep++;
    },
    setOverlaysHistory(state, action: PayloadAction<IOverlay[][]>) {
      state.liveStreamForm.history = action.payload;
    },
    setOverlaysHistoryStep(state, action: PayloadAction<number>) {
      state.liveStreamForm.historyStep = action.payload;
    },
    setLiveStreamStartDate(state, action: PayloadAction<dayjs.Dayjs>) {
      state.liveStreamForm.startDate = action.payload;
    },
    setLiveStreamEndDate(state, action: PayloadAction<dayjs.Dayjs>) {
      state.liveStreamForm.endDate = action.payload;
    },
    setLiveStreamTermsAgreedAt(state, action: PayloadAction<Date | null>) {
      state.liveStreamForm.termsAgreedAt = action.payload;
    },
    setLiveStreamContentAgreedAt(state, action: PayloadAction<Date | null>) {
      state.liveStreamForm.contentAgreedAt = action.payload;
    },
    setLiveStreamId(state, action: PayloadAction<string>) {
      state.liveStreamForm.id = action.payload;
    },
    setLiveStreamType(state, action: PayloadAction<LiveStreamTypeEnum>) {
      state.liveStreamForm.streamType = action.payload;
    },
    setLiveStreamOrientation(
      state,
      action: PayloadAction<LiveStreamOrientationEnum>
    ) {
      state.liveStreamForm.orientation = action.payload;
    },
    setVideoSettings(
      state,
      action: PayloadAction<{
        width: number;
        height: number;
        left: number;
        top: number;
      }>
    ) {
      state.liveStreamForm.videoWidth = action.payload.width;
      state.liveStreamForm.videoHeight = action.payload.height;
      state.liveStreamForm.videoLeft = action.payload.left;
      state.liveStreamForm.videoTop = action.payload.top;
    },
    setVideoBitrate(state, action: PayloadAction<number>) {
      state.liveStreamForm.videoBitrate = action.payload;
    },
    setAudioBitrate(state, action: PayloadAction<number>) {
      state.liveStreamForm.audioBitrate = action.payload;
    },
    setVideoFPS(state, action: PayloadAction<number>) {
      state.liveStreamForm.videoFPS = action.payload;
    },
    setResolution(state, action: PayloadAction<LiveStreamResolutionEnum>) {
      state.liveStreamForm.resolution = action.payload;
      // UI-SPEC Decision 3: switching to 1080p forces fps=30 (server enforces
      // this via isValidFps anyway). Switching to 4K leaves fps at its current
      // value (default 30 from initial state).
      if (action.payload === LiveStreamResolutionEnum.r1080p) {
        state.liveStreamForm.fps = 30;
      }
    },
    setFps(state, action: PayloadAction<number>) {
      state.liveStreamForm.fps = action.payload;
    },
    resetLiveStreamForm(state) {
      state.liveStreamForm = { ...initialState.liveStreamForm };
    },
    setLiveStreamForm(
      state,
      action: PayloadAction<LiveStreamState["liveStreamForm"]>
    ) {
      state.liveStreamForm = action.payload;
    },
    ensureLiveStreamWizardDraft(state, action: PayloadAction<string>) {
      if (!state.wizardDraftsByStream[action.payload]) {
        state.wizardDraftsByStream[action.payload] = {
          initialized: false,
          dirty: false,
          snapshot: null,
        };
      }
    },
    saveLiveStreamWizardDraft(
      state,
      action: PayloadAction<{
        streamId: string;
        snapshot: LiveStreamWizardDraftSnapshot;
        dirty: boolean;
        baseSnapshot?: LiveStreamWizardDraftSnapshot;
      }>
    ) {
      const previous = state.wizardDraftsByStream[action.payload.streamId];
      state.wizardDraftsByStream[action.payload.streamId] = {
        initialized: true,
        dirty: action.payload.dirty,
        snapshot: action.payload.snapshot,
        baseSnapshot:
          action.payload.baseSnapshot ??
          (action.payload.dirty
            ? previous?.baseSnapshot
            : action.payload.snapshot),
        baseContentRevision: previous?.baseContentRevision,
        baseFingerprint: previous?.baseFingerprint,
        // A51: a draft that was just saved is no longer stale.
        staleServerRevision: action.payload.dirty ? previous?.staleServerRevision : undefined,
        staleServerFingerprint: action.payload.dirty ? previous?.staleServerFingerprint : undefined,
        basePersistedTargets: previous?.basePersistedTargets,
        missingPersistedContent: action.payload.dirty ? previous?.missingPersistedContent : undefined,
      };
    },
    initializeLiveStreamWizardDraft(
      state,
      action: PayloadAction<{
        streamId: string;
        snapshot: LiveStreamWizardDraftSnapshot;
        baseContentRevision?: number;
        baseFingerprint?: string;
        basePersistedTargets?: PersistedContentTargets;
      }>
    ) {
      state.wizardDraftsByStream[action.payload.streamId] = {
        initialized: true,
        dirty: false,
        snapshot: action.payload.snapshot,
        baseSnapshot: action.payload.snapshot,
        baseContentRevision: action.payload.baseContentRevision,
        baseFingerprint: action.payload.baseFingerprint,
        basePersistedTargets: action.payload.basePersistedTargets,
      };
      state.activeWizardDraftId = action.payload.streamId;
    },
    setActiveLiveStreamWizardDraft(state, action: PayloadAction<string>) {
      state.activeWizardDraftId = action.payload;
    },
    /** A51: an edited draft is behind changed server data; the builder shows the banner. */
    flagStaleLiveStreamWizardDraft(
      state,
      action: PayloadAction<{
        streamId: string;
        serverRevision: number;
        serverFingerprint: string;
      }>
    ) {
      const draft = state.wizardDraftsByStream[action.payload.streamId];
      if (!draft) return;
      draft.staleServerRevision = action.payload.serverRevision;
      draft.staleServerFingerprint = action.payload.serverFingerprint;
    },
    flagMissingPersistedContent(state, action: PayloadAction<string>) {
      const draft = state.wizardDraftsByStream[action.payload];
      if (!draft) return;
      draft.dirty = true;
      draft.missingPersistedContent = true;
    },
    /** A51 "Keep my changes": accept that server copy as the draft's base. */
    keepLiveStreamWizardDraftChanges(state, action: PayloadAction<string>) {
      const draft = state.wizardDraftsByStream[action.payload];
      if (!draft || draft.missingPersistedContent || draft.staleServerRevision === undefined) return;
      draft.baseContentRevision = draft.staleServerRevision;
      draft.baseFingerprint = draft.staleServerFingerprint;
      if (state.liveStreamById?.id === action.payload) {
        draft.basePersistedTargets = persistedContentTargets(state.liveStreamById);
      }
      draft.staleServerRevision = undefined;
      draft.staleServerFingerprint = undefined;
    },
    /**
     * A51 review fix round 2: `updateLiveStream` calls this right after a
     * successful save whose draft is still dirty (an edit landed while the
     * save was in flight), using the PUT's own response as the new base —
     * never a later fetch, which could be a foreign change instead of this
     * tab's own write. Moves the base without touching the still-dirty
     * snapshot, so this tab's own save is never mistaken for an outside
     * change. Any stale flag referred to the pre-save base, so it's cleared
     * too. (A clean draft is re-initialized outright instead — see
     * `updateLiveStream` — so this reducer only runs on the dirty path.)
     */
    rebaseLiveStreamWizardDraftBase(
      state,
      action: PayloadAction<{
        streamId: string;
        baseContentRevision: number;
        baseFingerprint: string;
        basePersistedTargets?: PersistedContentTargets;
        baseSnapshot?: LiveStreamWizardDraftSnapshot;
      }>
    ) {
      const draft = state.wizardDraftsByStream[action.payload.streamId];
      if (!draft) return;
      draft.baseContentRevision = action.payload.baseContentRevision;
      draft.baseFingerprint = action.payload.baseFingerprint;
      if (action.payload.baseSnapshot) {
        draft.baseSnapshot = action.payload.baseSnapshot;
      }
      if (action.payload.basePersistedTargets) {
        draft.basePersistedTargets = action.payload.basePersistedTargets;
      }
      draft.staleServerRevision = undefined;
      draft.staleServerFingerprint = undefined;
      draft.missingPersistedContent = undefined;
    },
    /**
     * A51 review fix round 3 (Important 1): a save that commits for a stream
     * that is no longer on screen must not touch the live form or media
     * state — those belong to whichever stream is now active. When nothing
     * in the stored draft changed since the save was sent, there is nothing
     * left to preserve: drop it so the next visit re-initializes straight
     * from the server and picks up the ids the save created.
     */
    resetLiveStreamWizardDraft(state, action: PayloadAction<string>) {
      const draft = state.wizardDraftsByStream[action.payload];
      if (!draft) return;
      draft.initialized = false;
      draft.dirty = false;
      draft.snapshot = null;
      draft.baseSnapshot = undefined;
      draft.baseContentRevision = undefined;
      draft.baseFingerprint = undefined;
      draft.basePersistedTargets = undefined;
      draft.staleServerRevision = undefined;
      draft.staleServerFingerprint = undefined;
      draft.missingPersistedContent = undefined;
    },
    /**
     * A51 review fix round 3 (Important 1): updates a background stream's
     * stored draft snapshot only — never the live form/media state, which
     * belongs to whichever stream is on screen.
     */
    setStoredLiveStreamWizardDraftSnapshot(
      state,
      action: PayloadAction<{
        streamId: string;
        snapshot: LiveStreamWizardDraftSnapshot;
      }>
    ) {
      const draft = state.wizardDraftsByStream[action.payload.streamId];
      if (!draft) return;
      draft.snapshot = action.payload.snapshot;
    },
    setLiveStreamByIdLoading(state, action: PayloadAction<boolean>) {
      state.liveStreamByIdLoading = action.payload;
    },
    setLiveStreamById(state, action: PayloadAction<ILiveStream | null>) {
      state.liveStreamById = action.payload;
    },
    setLiveStreamByIdAnalytics(
      state,
      action: PayloadAction<ILiveStreamAnalytics[] | null>
    ) {
      state.liveStreamByIdAnalytics = action.payload;
    },
    setJingleEvery(state, action: PayloadAction<number>) {
      state.liveStreamForm.jingleEvery = action.payload;
    },
    setJingleEveryType(
      state,
      action: PayloadAction<LiveStreamJingleEveryTypeEnum>
    ) {
      state.liveStreamForm.jingleEveryType = action.payload;
    },
    setSecondaryVolume(state, action: PayloadAction<number>) {
      state.liveStreamForm.secondaryVideoVolume = action.payload;
    },
    setCRFMode(state, action: PayloadAction<boolean>) {
      state.liveStreamForm.crfMode = action.payload;
    },
    setVideoFadeInDuration(state, action: PayloadAction<number>) {
      state.liveStreamForm.videoFadeInDuration = action.payload;
    },
    setVideoFadeOutDuration(state, action: PayloadAction<number>) {
      state.liveStreamForm.videoFadeOutDuration = action.payload;
    },
    setAudioMixingEnabled(state, action: PayloadAction<boolean>) {
      state.liveStreamForm.audioMixingEnabled = action.payload;
    },
    setAudioMixingDuration(state, action: PayloadAction<number>) {
      state.liveStreamForm.audioMixingDuration = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestLiveStreamById.pending, (state, action) => {
        if (state.liveStreamById?.id !== action.meta.arg.id) {
          state.liveStreamById = null;
        }
        state.liveStreamByIdRequestedId = action.meta.arg.id;
        state.liveStreamByIdRequestId = action.meta.requestId;
        state.liveStreamByIdLoading = true;
        state.liveStreamByIdError = null;
        state.liveStreamByIdErrorStatus = null;
      })
      .addCase(requestLiveStreamById.fulfilled, (state, action) => {
        if (
          state.liveStreamByIdRequestedId !== action.meta.arg.id ||
          state.liveStreamByIdRequestId !== action.meta.requestId
        ) {
          return;
        }
        state.liveStreamById = action.payload;
        state.liveStreamByIdLoading = false;
        state.liveStreamByIdError = null;
        state.liveStreamByIdErrorStatus = null;
      })
      .addCase(requestLiveStreamById.rejected, (state, action) => {
        if (
          state.liveStreamByIdRequestedId !== action.meta.arg.id ||
          state.liveStreamByIdRequestId !== action.meta.requestId
        ) {
          return;
        }
        state.liveStreamByIdLoading = false;
        state.liveStreamByIdError =
          action.payload?.message ?? action.error.message ?? "Request failed";
        state.liveStreamByIdErrorStatus = action.payload?.status ?? null;
      });
  },
});

export const {
  setBackgroundId,
  setActiveLiveStreams,
  setLiveStreamOrientation,
  setVideoSettings,
  setVideoBitrate,
  setAudioBitrate,
  setVideoFPS,
  setActiveLiveStreamsLoading,
  setLiveStreamTitle,
  setLiveStreamPlaybackOrder,
  setLiveStreamStreamingPlatformsIds,
  setLiveStreamRepeatOnEnd,
  setLiveStreamStartOption,
  setLiveStreamEndOption,
  setLiveStreamOverlays,
  updateLiveStreamOverlay,
  setOverlaysHistory,
  setOverlaysHistoryStep,
  setLiveStreamStartDate,
  setLiveStreamEndDate,
  setLiveStreamTermsAgreedAt,
  setLiveStreamContentAgreedAt,
  setLiveStreamId,
  setLiveStreamByIdAnalytics,
  setLiveStreamType,
  resetLiveStreamForm,
  setLiveStreamForm,
  ensureLiveStreamWizardDraft,
  saveLiveStreamWizardDraft,
  initializeLiveStreamWizardDraft,
  setActiveLiveStreamWizardDraft,
  flagStaleLiveStreamWizardDraft,
  flagMissingPersistedContent,
  keepLiveStreamWizardDraftChanges,
  rebaseLiveStreamWizardDraftBase,
  resetLiveStreamWizardDraft,
  setStoredLiveStreamWizardDraftSnapshot,
  setUpdateLiveStreamLoading,
  setLiveStreamById,
  setLiveStreamByIdLoading,
  setSelectedPlatformId,
  setJingleEvery,
  setJingleEveryType,
  setLiveStreamJinglePlaybackOrder,
  setSecondaryVolume,
  setCRFMode,
  setVideoFadeInDuration,
  setVideoFadeOutDuration,
  setAudioMixingEnabled,
  setAudioMixingDuration,
  setResolution,
  setFps,
} = liveStreamSlice.actions;

type WizardDraftRootState = {
  liveStream: LiveStreamState;
  media: {
    selectedItems: IStreamSelectedItem[];
    selectedJingleItems: IStreamSelectedItem[];
    selectedSecondaryItems: IStreamSelectedItem[];
    selectedBackgroundId: string | null;
    scheduledShows: IScheduledShow[];
  };
};

function selectWizardDraftSnapshot(
  state: WizardDraftRootState
): LiveStreamWizardDraftSnapshot {
  return {
    liveStreamForm: state.liveStream.liveStreamForm,
    selectedItems: state.media.selectedItems,
    selectedJingleItems: state.media.selectedJingleItems,
    selectedSecondaryItems: state.media.selectedSecondaryItems,
    selectedBackgroundId: state.media.selectedBackgroundId,
    scheduledShows: state.media.scheduledShows,
  };
}

function wizardDraftSnapshotsEqual(
  left: LiveStreamWizardDraftSnapshot,
  right: LiveStreamWizardDraftSnapshot
) {
  // Probe, encode, and thumbnail results enrich selected rows without
  // changing the user's selection. Compare only fields edited by the builder.
  const selection = (items: IStreamSelectedItem[]) =>
    items.map(({ id, type, order }) => ({ id, type, order }));
  const comparable = (snapshot: LiveStreamWizardDraftSnapshot) => ({
    liveStreamForm: snapshot.liveStreamForm,
    selectedItems: selection(snapshot.selectedItems),
    selectedJingleItems: selection(snapshot.selectedJingleItems),
    selectedSecondaryItems: selection(snapshot.selectedSecondaryItems),
    selectedBackgroundId: snapshot.selectedBackgroundId,
    scheduledShows: snapshot.scheduledShows.map((show) => ({
      id: show.id,
      name: show.name,
      startMinuteOfDay: show.startMinuteOfDay,
      timezone: show.timezone,
      backgroundId: show.backgroundId,
      playlistId: show.playlistId,
      videoId: show.videoId,
      switchMode: show.switchMode,
      items: selection(show.items),
    })),
  });
  return JSON.stringify(comparable(left)) === JSON.stringify(comparable(right));
}

/** Only the active editor can have uncaptured form changes. */
export function selectLiveStreamWizardHasUnsavedChanges(
  state: WizardDraftRootState,
  streamId: string
): boolean {
  const draft = state.liveStream.wizardDraftsByStream[streamId];
  if (!draft?.initialized || !draft.snapshot) return false;
  const current =
    state.liveStream.activeWizardDraftId === streamId
      ? selectWizardDraftSnapshot(state)
      : draft.snapshot;
  return draft.baseSnapshot
    ? !wizardDraftSnapshotsEqual(draft.baseSnapshot, current)
    : draft.dirty || !wizardDraftSnapshotsEqual(draft.snapshot, current);
}

const captureActiveLiveStreamWizardDraft =
  () => (dispatch: any, getState: () => WizardDraftRootState) => {
    const state = getState();
    const streamId = state.liveStream.activeWizardDraftId;
    if (!streamId) {
      return;
    }
    const existing = state.liveStream.wizardDraftsByStream[streamId];
    if (!existing?.initialized || !existing.snapshot) {
      return;
    }
    const snapshot = selectWizardDraftSnapshot(state);
    dispatch(
      saveLiveStreamWizardDraft({
        streamId,
        snapshot,
        dirty: existing.baseSnapshot
          ? !wizardDraftSnapshotsEqual(existing.baseSnapshot, snapshot)
          : existing.dirty ||
            !wizardDraftSnapshotsEqual(existing.snapshot, snapshot),
      })
    );
  };

const restoreLiveStreamWizardDraft = (
  snapshot: LiveStreamWizardDraftSnapshot,
  dispatch: any
) => {
  dispatch(setLiveStreamForm(snapshot.liveStreamForm));
  dispatch(setSelectedItems(snapshot.selectedItems));
  dispatch(setSelectedJingleItems(snapshot.selectedJingleItems));
  dispatch(setSelectedSecondaryItems(snapshot.selectedSecondaryItems));
  dispatch(setSelectedBackgroundId(snapshot.selectedBackgroundId));
  dispatch(setShows(snapshot.scheduledShows));
};

/**
 * A51: an unedited draft never hides newer server content. An edited draft
 * is kept; Task 20 flags it so the builder can offer Reload / Keep my changes.
 */
const serverIsNewer = (
  draft: LiveStreamWizardDraft | undefined,
  liveStream: ILiveStream
) =>
  !!draft?.initialized &&
  typeof liveStream.contentRevision === "number" &&
  liveStream.contentRevision > (draft.baseContentRevision ?? -1);

const isStaleCleanDraft = (
  draft: LiveStreamWizardDraft | undefined,
  liveStream: ILiveStream
) => serverIsNewer(draft, liveStream) && !draft!.dirty;

const activateLiveStreamWizardDraft =
  ({ liveStream }: { liveStream: ILiveStream }) =>
  (dispatch: any, getState: () => WizardDraftRootState) => {
    const streamId = liveStream.id;
    const wasActive = getState().liveStream.activeWizardDraftId === streamId;

    dispatch(captureActiveLiveStreamWizardDraft());
    const existing = getState().liveStream.wizardDraftsByStream[streamId];
    const stale = isStaleCleanDraft(existing, liveStream);

    // A51: keep an edited draft, but flag it when the data it was built from
    // changed on the server. Uploads, probes and encodes leave the fingerprint
    // alone. A copy fetched while this tab's own save is in flight is not
    // used to rebase or to flag: it could be the post-save refetch (already
    // rebased directly from `updateLiveStream`'s `saved` response — see
    // review fix round 2), or a foreign change that landed during the save's
    // multiple requests, which this fetch cannot tell apart from our own
    // write. Ignoring it here means the next non-loading fetch still compares
    // against the correct base and can still flag a genuine foreign change.
    if (
      existing?.dirty &&
      existing.baseFingerprint !== undefined &&
      !getState().liveStream.updateLiveStreamLoading &&
      serverIsNewer(existing, liveStream)
    ) {
      const serverFingerprint = draftSourceFingerprint(liveStream);
      if (
        serverFingerprint !== existing.baseFingerprint &&
        serverFingerprint !== existing.staleServerFingerprint
      )
        dispatch(
          flagStaleLiveStreamWizardDraft({
            streamId,
            serverRevision: liveStream.contentRevision!,
            serverFingerprint,
          })
        );
    }

    if (wasActive && !stale) {
      return;
    }
    if (existing?.initialized && existing.snapshot && !stale) {
      restoreLiveStreamWizardDraft(existing.snapshot, dispatch);
      dispatch(setActiveLiveStreamWizardDraft(streamId));
      return;
    }

    dispatch(populateLiveStreamSettingsFromLiveStream({ liveStream }));
    dispatch(
      initializeLiveStreamWizardDraft({
        streamId,
        snapshot: selectWizardDraftSnapshot(getState()),
        baseContentRevision: liveStream.contentRevision,
        baseFingerprint: draftSourceFingerprint(liveStream),
        basePersistedTargets: persistedContentTargets(liveStream),
      })
    );
  };

/**
 * After a successful save the draft matches what was actually sent. Mark it
 * clean only if nothing changed since `savedSnapshot` was captured (at the
 * start of `updateLiveStream`, before any await): an edit that lands while
 * the save is still in flight must stay dirty, not be silently stamped clean
 * and then dropped by the post-save re-fetch (A51).
 */
export const markLiveStreamWizardDraftSaved =
  (streamId: string, savedSnapshot?: LiveStreamWizardDraftSnapshot) =>
  (dispatch: any, getState: () => WizardDraftRootState) => {
    const state = getState();
    const existing = state.liveStream.wizardDraftsByStream[streamId];
    if (
      state.liveStream.activeWizardDraftId !== streamId ||
      !existing?.initialized
    )
      return;
    const current = selectWizardDraftSnapshot(state);
    dispatch(
      saveLiveStreamWizardDraft({
        streamId,
        snapshot: current,
        dirty: savedSnapshot
          ? !wizardDraftSnapshotsEqual(savedSnapshot, current)
          : false,
        baseSnapshot: savedSnapshot ?? current,
      })
    );
  };

/** A51 "Reload": replace the edited draft with the latest server copy. */
export const reloadLiveStreamWizardDraft =
  (streamId: string) =>
  (dispatch: any, getState: () => WizardDraftRootState) => {
    const liveStream = getState().liveStream.liveStreamById;
    if (!liveStream || liveStream.id !== streamId) return;
    dispatch(populateLiveStreamSettingsFromLiveStream({ liveStream }));
    dispatch(
      initializeLiveStreamWizardDraft({
        streamId,
        snapshot: selectWizardDraftSnapshot(getState()),
        baseContentRevision: liveStream.contentRevision,
        baseFingerprint: draftSourceFingerprint(liveStream),
        basePersistedTargets: persistedContentTargets(liveStream),
      })
    );
  };

export const getLiveStreamById =
  (args: { id: string; silent: boolean }) => (dispatch: any) => {
    dispatch(captureActiveLiveStreamWizardDraft());
    dispatch(ensureLiveStreamWizardDraft(args.id));
    return dispatch(requestLiveStreamById(args));
  };

/** Editor Back: refresh first, then drop only this stream's draft. */
export const discardLiveStreamWizardChanges =
  (streamId: string) =>
  async (dispatch: any, getState: () => WizardDraftRootState) => {
    const before = getState().liveStream;
    if (
      before.updateLiveStreamLoading ||
      before.liveStreamById?.id !== streamId ||
      (before.activeWizardDraftId && before.activeWizardDraftId !== streamId)
    ) {
      return false;
    }

    const result = await dispatch(getLiveStreamById({ id: streamId, silent: false }));
    if (requestLiveStreamById.rejected.match(result) && result.payload?.status === 404) {
      // Deleted in another tab. The refresh shows no toast for a 404 (the page has its own
      // "Stream not found"), but the editor stays open here, so say why Back did nothing.
      notification.error({ message: i18n.t("liveStream:detail.notFound.title") });
      return false;
    }
    const current = getState().liveStream;
    if (
      !requestLiveStreamById.fulfilled.match(result) ||
      current.updateLiveStreamLoading ||
      current.liveStreamById?.id !== streamId ||
      current.liveStreamByIdRequestId !== result.meta.requestId ||
      current.activeWizardDraftId !== streamId
    ) {
      return false;
    }

    dispatch(resetLiveStreamWizardDraft(streamId));
    dispatch(reloadLiveStreamWizardDraft(streamId));
    return true;
  };

export const getActiveLiveStreams = createAsyncThunk(
  "liveStream/getActiveLiveStreams",
  async (silent: boolean, { dispatch, getState }) => {
    if (!silent) {
      dispatch(setActiveLiveStreamsLoading(true));
    }
    try {
      const response = await LiveStreamServiceInstance.getActiveLiveStreams();
      dispatch(setActiveLiveStreams(response.data));
    } catch (err: any) {
      // With streams on screen a toast is enough. With none, the page (which unwraps this
      // rejection) shows ApiErrorState with Retry instead.
      const shown = (getState() as { liveStream: LiveStreamState }).liveStream.activeLiveStreams;
      if (shown.length) dispatchErrorNotification(err);
      throw err;
    } finally {
      if (!silent) {
        dispatch(setActiveLiveStreamsLoading(false));
      }
    }
  }
);
export const getLiveStreamsAnalytics = createAsyncThunk(
  "liveStream/getLiveStreamsAnalytics",
  async (
    {
      id,
      startDate,
      endDate,
    }: { id: string; startDate: string; endDate: string },
    { dispatch }
  ) => {
    try {
      const response =
        await LiveStreamServiceInstance.getLiveStreamAnalyticsById(
          id,
          startDate,
          endDate
        );
      dispatch(setLiveStreamByIdAnalytics(response.data));
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
  }
);

export const populateLiveStreamSettingsFromLiveStream = createAsyncThunk(
  "liveStream/populateLiveStreamSettingsFromLiveStream",
  async ({ liveStream }: { liveStream: ILiveStream }, { dispatch }) => {
    let title = "";
    if (liveStream.type === LiveStreamTypeEnum.video && liveStream.video) {
      title = liveStream.video.name;
      dispatch(
        setSelectedItems(
          liveStream.video.audios
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((audio) => ({
              ...audio.audio,
              order: audio.order,
              type: "audio",
            }))
        )
      );
      if (liveStream.jingleVideo) {
        dispatch(
          setSelectedJingleItems(
            liveStream.jingleVideo.audios
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((audio) => ({
                ...audio.audio,
                id: getJingleItemId(audio.audio.id),
                order: audio.order,
                type: "audio",
              }))
          )
        );
      } else {
        dispatch(setSelectedJingleItems([]));
      }
    } else if (
      liveStream.type === LiveStreamTypeEnum.playlist &&
      liveStream.playlist
    ) {
      title = liveStream.playlist.name;
      dispatch(
        setSelectedItems(
          liveStream.playlist.playlistVisualMedias
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((visualMedia) => ({
              ...visualMedia.visualMedia,
              order: visualMedia.order,
              url: visualMedia.visualMedia.url || "",
              thubmnailUrl: visualMedia.visualMedia.thubmnailUrl || "",
              type: "video",
            }))
        )
      );
      if (liveStream.jinglePlaylist) {
        dispatch(
          setSelectedJingleItems(
            liveStream.jinglePlaylist.playlistVisualMedias
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((visualMedia) => ({
                ...visualMedia.visualMedia,
                id: getJingleItemId(visualMedia.visualMedia.id),
                order: visualMedia.order,
                url: visualMedia.visualMedia.url || "",
                thubmnailUrl: visualMedia.visualMedia.thubmnailUrl || "",
                type: "video",
              }))
          )
        );
      } else {
        dispatch(setSelectedJingleItems([]));
      }
    } else {
      dispatch(setSelectedItems([]));
      dispatch(setSelectedJingleItems([]));
    }

    dispatch(
      setSelectedBackgroundId(
        liveStream.type === LiveStreamTypeEnum.video
          ? liveStream.video?.backgrounds[0]?.visualMediaId ?? null
          : null
      )
    );

    if (liveStream.secondaryVideo) {
      dispatch(
        setSelectedSecondaryItems(
          liveStream.secondaryVideo.audios
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((audio) => ({
              ...audio.audio,
              id: getSecondaryItemId(audio.audio.id),
              order: audio.order,
              type: "audio",
            }))
        )
      );
    } else {
      dispatch(setSelectedSecondaryItems([]));
    }

    dispatch(
      setShows(
        liveStream.liveStreamScheduledShows.map((show) => ({
          id: show.id,
          name: show.name,
          startMinuteOfDay: show.startMinuteOfDay,
          timezone: show.timezone,
          playlistId: show.playlistId,
          videoId: show.videoId,
          backgroundId: show.video?.backgrounds[0]?.visualMediaId || "",
          switchMode: show.switchMode || "wait",
          items: show.playlist
            ? show.playlist?.playlistVisualMedias
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((visualMedia) => ({
                  ...visualMedia.visualMedia,
                  id: getShowItemId(show.id, visualMedia.visualMedia.id),
                  type: FolderTypeEnum.video,
                  url: visualMedia.visualMedia.url || "",
                  thubmnailUrl: visualMedia.visualMedia.thubmnailUrl || "",
                  order: visualMedia.order,
                }))
            : show.video
            ? show.video?.audios
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((audio) => ({
                  ...audio.audio,
                  id: getShowItemId(show.id, audio.audio.id),
                  type: FolderTypeEnum.audio,
                  order: audio.order,
                }))
            : [],
        }))
      )
    );

    dispatch(
      setSelectedPlatformId(liveStream.ownedDestinations[0]?.id || null)
    );
    dispatch(
      setLiveStreamForm({
        id: liveStream.id,
        backgroundId: liveStream.video?.backgrounds[0]?.visualMediaId || "",
        overlays: (
          liveStream.video?.overlays ||
          liveStream.playlist?.overlays ||
          []
        ).map((overlay) => ({ ...overlay, isSelected: false })),
        history: [
          (
            liveStream.video?.overlays ||
            liveStream.playlist?.overlays ||
            []
          ).map((overlay) => ({ ...overlay, isSelected: false })),
        ],
        historyStep: 0,
        playbackOrder: liveStream.playbackOrder,
        // endOption is canonical. Keep the legacy form value derived for old
        // draft consumers while the settings radio and update payload use
        // endOption directly.
        repeatOnEnd:
          liveStream.endOption !== LiveStreamEndOptionEnum.contentEnd,
        streamingPlatformsIds: liveStream.ownedDestinations
          .filter((d) => d.enabled)
          .map((d) => d.id),
        title,
        startOption: liveStream.startOption,
        endOption: liveStream.endOption,
        startDate: liveStream.startDate
          ? dayjs(liveStream.startDate)
          : undefined,
        endDate: liveStream.endDate ? dayjs(liveStream.endDate) : undefined,
        streamType: liveStream.type,
        // A40: a stream that never agreed reloads unchecked, not as 1970.
        termsAgreedAt: liveStream.termsAgreedAt
          ? new Date(liveStream.termsAgreedAt)
          : null,
        contentAgreedAt: liveStream.contentAgreedAt
          ? new Date(liveStream.contentAgreedAt)
          : null,
        orientation:
          liveStream.orientation || LiveStreamOrientationEnum.horizontal,
        videoWidth: liveStream.videoWidth || CONTAINER_WIDTH,
        videoHeight: liveStream.videoHeight || CONTAINER_HEIGHT,
        videoLeft: liveStream.videoLeft || 0,
        videoTop: liveStream.videoTop || 0,
        // A46: same fallback as the api (7000 kb/s).
        videoBitrate: liveStream.videoBitrate || DEFAULT_VIDEO_BITRATE,
        audioBitrate: liveStream.audioBitrate || 320,
        videoFPS: liveStream.videoFPS || 30,
        resolution: liveStream.resolution || LiveStreamResolutionEnum.r1080p,
        fps: liveStream.fps || 30,
        jingleEvery: liveStream.jingleEvery >= 1 ? liveStream.jingleEvery : 10,
        // Decision #12 — jingles always count songs.
        jingleEveryType: LiveStreamJingleEveryTypeEnum.songs,
        jinglePlaybackOrder:
          liveStream.jinglePlaybackOrder ||
          LiveStreamPlaybackOrderEnum.sequential,
        // Decision #15 — 0–100%; 0 is a real choice.
        secondaryVideoVolume: Math.min(
          100,
          liveStream.secondaryVideoVolume ?? 20
        ),
        crfMode: liveStream.crfMode || false,
        videoFadeInDuration: liveStream.videoFadeInDuration || 0,
        videoFadeOutDuration: liveStream.videoFadeOutDuration || 0,
        audioMixingEnabled: liveStream.audioMixingEnabled || false,
        audioMixingDuration: liveStream.audioMixingDuration || 0,
        isGST: liveStream.isGST,
      })
    );
  }
);

function formatOverlays(
  overlays: IOverlaySelectable[],
  preservePersisted = false
) {
  return overlays
    .filter(
      (overlay) =>
        preservePersisted ||
        ([
          OverlayTypeEnum.gif,
          OverlayTypeEnum.image,
          OverlayTypeEnum.video,
        ].includes(overlay.type) &&
          overlay.visualMediaId) ||
        ![
          OverlayTypeEnum.gif,
          OverlayTypeEnum.image,
          OverlayTypeEnum.video,
        ].includes(overlay.type)
    )
    .map((overlay) => {
      let settings = undefined;

      if (preservePersisted ? overlay.settings != null : overlay.settings) {
        if (typeof overlay.settings === "string") {
          try {
            if (preservePersisted || JSON.parse(overlay.settings)) {
              settings = overlay.settings;
            }
          } catch (err) {}
        } else {
          // Stream detail returns Prisma JSON objects; the write DTO accepts
          // a JSON string. Preserve the hidden settings when re-saving.
          settings = JSON.stringify(overlay.settings);
        }
      }

      return {
        ...overlay,
        isSelected: undefined,
        visualMedia: undefined,
        url: undefined,
        id: undefined,
        videoId: undefined,
        playlistId: undefined,
        settings,
      };
    });
}

/** The API treats omitted keys as unchanged; hidden fields must not be echoed from a stale draft. */
function omitHiddenPlanFields(
  body: IUpdateLiveStreamRequest,
  preserveProDestinations: boolean
): IUpdateLiveStreamRequest {
  const hiddenKeys = [
    "liveStreamScheduledShows",
    "jingleEvery",
    "jingleEveryType",
    "jinglePlaybackOrder",
    "jinglePlaylistId",
    "jingleVideoId",
    "secondaryVideoId",
    "secondaryVideoVolume",
    "orientation",
    "videoWidth",
    "videoHeight",
    "videoLeft",
    "videoTop",
    "videoBitrate",
    "videoFPS",
    "audioBitrate",
    "videoFadeInDuration",
    "videoFadeOutDuration",
    "audioMixingEnabled",
    "audioMixingDuration",
    "crfMode",
  ] as const satisfies readonly (keyof IUpdateLiveStreamRequest)[];
  for (const key of hiddenKeys) delete body[key];
  // Basic users can edit their one destination. Existing Pro streams may
  // have additional enabled destinations that the Basic-only UI cannot show.
  if (preserveProDestinations) delete body.streamingPlatformsIds;
  return body;
}

/**
 * update() commits the settings before its post-save `switch` runs
 * (live-stream.service.ts's `update()`), so a refusal from any branch of that
 * switch — not only the start gate — means "saved, but the follow-up action
 * was refused", not "refused, nothing written":
 *  - AUTH_EMAIL_NOT_VERIFIED (403): assertEmailVerified, called from
 *    `case 'start'` / `case 'retry_provision'`.
 *  - STREAM_START_BLOCKED: thrown inside start()/retryProvision() (and inside
 *    claimLegacyRedispatch, which `case 'refresh'`/`case 'restart_worker'`
 *    reach via refreshLiveStream()/handleStreamRestart()).
 *  - STREAM_NOT_FAILED: retryProvision() only.
 *  - STREAM_TEARDOWN_PENDING: thrown by assertDispatcherStartAllowed, called
 *    from both start() and retryProvision().
 *  - WORKER_PAYLOAD_INVALID (422): building the payload
 *    streamWorkerService.create() sends, awaited inside start()/
 *    retryProvision().
 *  - RUNTIME_INSPECTION_REQUIRED / STREAM_NOT_RUNNING: thrown by
 *    claimLegacyRedispatch (case 'refresh' / 'restart_worker' only — fix
 *    round 1, controller ruling R1).
 * A code-less 409 ("Stream cleanup requires inspection[...]", thrown by
 * start()/retryProvision()/claimLegacyRedispatch) is also a genuine
 * post-commit refusal; it carries no `code`, so it is matched by HTTP status
 * instead. See the report for Task 21 (fix round 1) for the exact call sites;
 * line numbers are deliberately left out of this comment since they drift.
 */
const SAVED_THEN_POST_COMMIT_REFUSED = new Set([
  "AUTH_EMAIL_NOT_VERIFIED",
  "STREAM_START_BLOCKED",
  "STREAM_NOT_FAILED",
  "STREAM_TEARDOWN_PENDING",
  "WORKER_PAYLOAD_INVALID",
  "RUNTIME_INSPECTION_REQUIRED",
  "STREAM_NOT_RUNNING",
]);
const errorCodeOf = (err: any): string | undefined =>
  err?.code ?? err?.response?.data?.code;
const errorStatusOf = (err: any): number | undefined =>
  err?.status ?? err?.response?.status;
const isPostCommitRefusal = (err: any): boolean => {
  const code = errorCodeOf(err);
  return code
    ? SAVED_THEN_POST_COMMIT_REFUSED.has(code)
    : errorStatusOf(err) === 409;
};

/**
 * A "new…" show this save just created now has a real server id (in
 * `saved`), matched by the name this save sent. Task 20 remapped `show.id`;
 * review fix round 3 (Minor 1) also rebuilds its items' ids, which are
 * derived from the show id (`getShowItemId`) and would otherwise still name
 * the old placeholder — leaving item remove/checkbox/drag-out a no-op until
 * the next save. Returns the same `show` reference when there's nothing to
 * remap, so callers can detect a change with `!==`.
 */
const remapNewShowToCreated = (
  show: IScheduledShow,
  savedSnapshot: LiveStreamWizardDraftSnapshot,
  createdShowsByName: Map<string, ILiveStreamScheduledShow>
): IScheduledShow => {
  if (!show.id.startsWith("new")) return show;
  const sentAsShow = savedSnapshot.scheduledShows.find(
    (sent) => sent.id === show.id
  );
  const created = sentAsShow && createdShowsByName.get(sentAsShow.name);
  if (!created) return show;
  return {
    ...show,
    id: created.id,
    videoId: created.videoId,
    playlistId: created.playlistId,
    items: show.items.map((item) => ({
      ...item,
      id: getShowItemId(created.id, parseShowItemId(item.id).itemId),
    })),
  };
};

/**
 * Review fix round 3 (Important 1): applies a completed save for a stream
 * that is no longer the one on screen (the user switched away while the save
 * was in flight). Only the stored draft entry for `id` in
 * `wizardDraftsByStream` is touched — never the live form, media state,
 * `liveStreamById` or `activeWizardDraftId`, which all belong to whichever
 * stream is now active. Mirrors how `refreshStreamSnapshot` refuses to let a
 * background mutation for one stream replace the detail being displayed for
 * another.
 */
const applyBackgroundLiveStreamSave = (
  id: string,
  saved: ILiveStream,
  savedSnapshot: LiveStreamWizardDraftSnapshot,
  dispatch: any,
  getState: () => RootState
) => {
  const existing = getState().liveStream.wizardDraftsByStream[id];
  if (!existing?.initialized || !existing.snapshot) return;
  if (wizardDraftSnapshotsEqual(existing.snapshot, savedSnapshot)) {
    // Nothing in the stored draft changed after this save was sent: there is
    // nothing left to preserve, so drop it and let the next visit
    // re-initialize straight from the server (picking up ids this save
    // created, without us having to guess every one of them here).
    dispatch(resetLiveStreamWizardDraft(id));
    return;
  }
  // The stored draft still differs from what was sent (edits captured just
  // before the switch away landed in it). Keep it dirty, but remap any
  // "new…" show this save just created a real id for, inside the stored
  // snapshot only — never the live media state.
  const createdShowsByName = new Map(
    (saved.liveStreamScheduledShows ?? []).map((show) => [show.name, show])
  );
  const remappedShows = existing.snapshot.scheduledShows.map((show) =>
    remapNewShowToCreated(show, savedSnapshot, createdShowsByName)
  );
  dispatch(
    setStoredLiveStreamWizardDraftSnapshot({
      streamId: id,
      snapshot: { ...existing.snapshot, scheduledShows: remappedShows },
    })
  );
  // Rebase from the copy that reflects what was actually committed, not from
  // whatever a later refetch happens to return.
  dispatch(
    rebaseLiveStreamWizardDraftBase({
      streamId: id,
      baseContentRevision: saved.contentRevision!,
      baseFingerprint: draftSourceFingerprint(saved),
      basePersistedTargets: persistedContentTargets(saved),
      baseSnapshot: savedSnapshot,
    })
  );
};

/**
 * Applies what a save actually committed to the wizard draft and the live
 * redux state — from the PUT's own `saved` response on success, or (Task 21:
 * B4 x B5) from a re-fetch when the final PUT's post-save start gate refused
 * the start but the settings underneath had already been written.
 */
const applyCommittedLiveStreamSave = (
  id: string,
  saved: ILiveStream,
  savedSnapshot: LiveStreamWizardDraftSnapshot,
  dispatch: any,
  getState: () => RootState
) => {
  // Review fix round 3 (Important 1): the user may have switched to another
  // stream while this save was in flight. That stream's form, media state
  // and activeWizardDraftId must not be touched by A's own save landing late.
  if (getState().liveStream.activeWizardDraftId !== id) {
    applyBackgroundLiveStreamSave(id, saved, savedSnapshot, dispatch, getState);
    return;
  }
  dispatch(markLiveStreamWizardDraftSaved(id, savedSnapshot));
  // What happens next depends on whether an edit landed while this save was
  // in flight (the draft stayed dirty).
  if (getState().liveStream.wizardDraftsByStream[id]?.dirty) {
    // (b) Keep the user's in-flight edits. But a "new…" show this save just
    // created now has a real server id (in `saved`), so remap it in the live
    // scheduledShows too — matched by the name this save sent — or the next
    // save would partition it as new again and create a duplicate show
    // video/playlist.
    const createdShowsByName = new Map(
      (saved.liveStreamScheduledShows ?? []).map((show) => [show.name, show])
    );
    const liveShows = getState().media.scheduledShows;
    let showsChanged = false;
    const remappedShows = liveShows.map((show) => {
      const remapped = remapNewShowToCreated(
        show,
        savedSnapshot,
        createdShowsByName
      );
      if (remapped !== show) showsChanged = true;
      return remapped;
    });
    if (showsChanged) {
      dispatch(setShows(remappedShows));
      // Keep the draft's own stored snapshot in sync with the remap.
      dispatch(captureActiveLiveStreamWizardDraft());
    }
    // Rebase from the copy that reflects what was actually committed, not
    // from whatever a later refetch happens to return. A foreign change
    // landing after this must still be flagged by that refetch, not silently
    // folded into the base.
    dispatch(
      rebaseLiveStreamWizardDraftBase({
        streamId: id,
        baseContentRevision: saved.contentRevision!,
        baseFingerprint: draftSourceFingerprint(saved),
        basePersistedTargets: persistedContentTargets(saved),
      })
    );
  } else {
    // (a) A clean draft has nothing to lose: re-initialize it straight from
    // what was saved — like Task 16's clean-draft replacement — instead of
    // leaving it to a later refetch. That refetch would see the same
    // revision this save just wrote, so `serverIsNewer` would be false and
    // `activateLiveStreamWizardDraft` would return without re-populating,
    // leaving server-assigned ids (e.g. a new scheduled show's real id)
    // stuck at their client "new…" placeholder until the user navigated away
    // and back.
    dispatch(populateLiveStreamSettingsFromLiveStream({ liveStream: saved }));
    dispatch(
      initializeLiveStreamWizardDraft({
        streamId: id,
        snapshot: selectWizardDraftSnapshot(getState()),
        baseContentRevision: saved.contentRevision,
        baseFingerprint: draftSourceFingerprint(saved),
        basePersistedTargets: persistedContentTargets(saved),
      })
    );
  }
};

export const updateLiveStream = createAsyncThunk(
  "liveStream/updateLiveStream",
  async (
    {
      id,
      entitlement,
      form,
      selectedItems,
      selectedJingleItems,
      selectedSecondaryItems,
      scheduledShows,
      notificationMode = "save",
    }: {
      id: string;
      entitlement: StreamEntitlement;
      form: LiveStreamState["liveStreamForm"];
      selectedItems: IStreamSelectedItem[];
      scheduledShows: IScheduledShow[];
      selectedJingleItems: IStreamSelectedItem[];
      selectedSecondaryItems: IStreamSelectedItem[];
      notificationMode?: "save" | "goLive";
    },
    { dispatch, getState }
  ) => {
    // A51 fix round 1: capture what's actually in the draft before any
    // await, so an edit that lands while this save is in flight is not
    // mistaken for the state that was saved.
    const savedSnapshot = selectWizardDraftSnapshot(getState() as RootState);
    const stateAtSave = (getState() as RootState).liveStream;
    const current = stateAtSave.liveStreamById?.id === id
      ? stateAtSave.liveStreamById
      : null;
    const draft = stateAtSave.wizardDraftsByStream[id];
    const hideProFeatures = !ENABLE_PRO_PLAN;
    const preserveExistingPro = hideProFeatures && entitlement.plan !== "basic";
    // An audio stream's primary content is hidden with Pro. It cannot be
    // reconstructed from the Basic-only editor, even after a downgrade.
    if (hideProFeatures && (
      current?.type === LiveStreamTypeEnum.video ||
      form.streamType === LiveStreamTypeEnum.video ||
      (preserveExistingPro && (!current ||
        current.type !== LiveStreamTypeEnum.playlist ||
        form.streamType !== LiveStreamTypeEnum.playlist))
    )) {
      throw new Error("DRAFT_SERVER_CHANGED");
    }
    let videoId = current?.videoId ?? undefined;
    let jingleVideoId = current?.jingleVideoId ?? undefined;
    let jinglePlaylistId = current?.jinglePlaylistId ?? undefined;
    let secondaryVideoId = current?.secondaryVideoId ?? undefined;
    const readyPrimaryBackground =
      form.streamType === LiveStreamTypeEnum.playlist
        ? selectedItems.find(
            (item) =>
              item.type === "video" &&
              item.resolutionTier != null &&
              !item.resolutionError &&
              item.duration > 0 &&
              !exceedsStreamTier(item, form.resolution),
          )?.id
        : undefined;
    const secondaryVideoVisualMediaId =
      form.streamType === LiveStreamTypeEnum.playlist
        ? readyPrimaryBackground ??
          current?.secondaryVideo?.backgrounds[0]?.visualMediaId
        : form.backgroundId;
    if (
      !hideProFeatures &&
      form.streamType === LiveStreamTypeEnum.playlist &&
      entitlement.features.secondaryVideo &&
      selectedSecondaryItems.length > 0 &&
      !secondaryVideoVisualMediaId
    ) {
      notification.warning({
        message: i18n.t("liveStream:stepper.secondaryCard.pendingBackground"),
      });
      throw new Error("SECONDARY_BACKGROUND_PENDING");
    }
    // B4: an empty show would 400 in its batch write after the primary
    // playlist was already saved. Refuse it before any request.
    const emptyShow = hideProFeatures ? undefined : findEmptyScheduledShow(
      scheduledShows,
      entitlement.features.scheduledShows
    );
    if (emptyShow) {
      notification.error({
        message: i18n.t("liveStream:stepper.scheduledShow.emptyShow", {
          name: emptyShow.name,
        }),
      });
      throw new Error("SCHEDULED_SHOW_EMPTY");
    }
    dispatch(setUpdateLiveStreamLoading(true));
    // Task 21 (B4 x B5): once the final PUT below has been sent, update()
    // has already committed the settings — any refusal from its post-save
    // start gate means "saved, but not started", not "refused, nothing
    // written". Everything before it (this validateOnly pre-check, the
    // content writes) can still leave the draft dirty on failure as before.
    let finalPutSent = false;
    try {
      // The primary playlist is committed together with the stream PUT. The
      // generic playlist endpoints remain strict for shows and jingles.
      let freshContent: ILiveStream | null = null;
      // A wizard can still hold model/show ids removed by another tab,
      // including a clean draft that was never polled after deletion.
      // Check a fresh detail before any validation or content write; a
      // previously detected conflict remains blocked until Reload replaces
      // the draft, even if the user chose Keep for another stale change.
      if (!hideProFeatures && draft?.missingPersistedContent) {
        throw new Error("DRAFT_CONTENT_MISSING");
      }
      if (current?.id === id) {
        const { data: fresh } =
          await LiveStreamServiceInstance.getLiveStreamById(id);
        freshContent = fresh;
        const expected = draft?.basePersistedTargets ?? persistedContentTargets(current);
        // Hidden Pro collections may have changed in another tab. We omit
        // them from this save, so only a changed primary target conflicts.
        const targetStatus = hideProFeatures
          ? expected.playlistId === fresh.playlistId
            ? "same"
            : expected.playlistId && !fresh.playlistId
              ? "deleted"
              : "changed"
          : draftTargetStatus(expected, fresh, scheduledShows);
        if (targetStatus !== "same") {
          if ((getState() as RootState).liveStream.liveStreamById?.id === id) {
            dispatch(setLiveStreamById(fresh));
          }
          if (targetStatus === "deleted") {
            dispatch(flagMissingPersistedContent(id));
            throw new Error("DRAFT_CONTENT_MISSING");
          }
          dispatch(flagStaleLiveStreamWizardDraft({
            streamId: id,
            serverRevision: fresh.contentRevision ?? draft?.baseContentRevision ?? 0,
            serverFingerprint: draftSourceFingerprint(fresh),
          }));
          throw new Error("DRAFT_SERVER_CHANGED");
        }
      }
      // The stream detail includes the current primary playlist and overlays.
      // An incomplete detail cannot safely replace that playlist while its
      // overlay controls are hidden.
      if (preserveExistingPro && (
        freshContent?.id !== id ||
        freshContent.type !== LiveStreamTypeEnum.playlist ||
        (freshContent.playlistId ?? null) !== (freshContent.playlist?.id ?? null) ||
        (freshContent.playlistId != null &&
          !Array.isArray(freshContent.playlist?.overlays))
      )) {
        throw new Error("DRAFT_SERVER_CHANGED");
      }
      const overlaysPayload = {
        overlays: entitlement.overlaysEditable
          ? formatOverlays(
              preserveExistingPro
                ? freshContent?.playlist?.overlays ?? []
                : form.overlays || [],
              preserveExistingPro
            )
          : [],
      };
      const primaryPlaylist: ICreatePlaylistRequest | undefined =
        form.streamType === LiveStreamTypeEnum.playlist
          ? {
              name: form.title,
              playlistVisualMedias: selectedItems.map((item) => ({
                visualMediaId: item.id,
                order: item.order,
              })),
              ...overlaysPayload,
            }
          : undefined;
      const preserveDestinations = preserveExistingPro ||
        (freshContent?.ownedDestinations?.filter((destination) => destination.enabled)
          .length ?? 0) > 1;
      const settingsBody = (
        shows: Parameters<typeof buildLiveStreamSettingsBody>[0]["liveStreamScheduledShows"]
      ) => {
        const body = buildLiveStreamSettingsBody({
          id,
          form,
          entitlement,
          ids: {
            videoId,
            jinglePlaylistId,
            jingleVideoId,
            secondaryVideoId,
          },
          liveStreamScheduledShows: shows,
          primaryPlaylist,
        });
        return hideProFeatures
          ? omitHiddenPlanFields(body, preserveDestinations)
          : body;
      };
      // B4: run every settings gate (plan, destinations, schedule, shows)
      // before any content write, so a refused save leaves nothing half-applied.
      await LiveStreamServiceInstance.validateLiveStreamUpdate(
        settingsBody(hideProFeatures ? [] : precheckScheduledShows(
            scheduledShows,
            form.streamType,
            entitlement.features.scheduledShows
          ))
      );
      let liveStreamScheduledShows: ILiveStreamScheduledShow[] = [];

      if (form.streamType === LiveStreamTypeEnum.video) {
        let video: IVideo | null = null;
        if (videoId) {
          const data: IUpdateVideoRequest = {
            id: videoId,
            name: form.title,
            fadeBetweenBackgrounds: false,
            export: false,
            backgrounds: [
              {
                visualMediaId: form.backgroundId!,
                order: 0,
                startSecond: 0,
                endSecond: 20,
              },
            ],
            audios: selectedItems.map((item) => ({
              audioId: item.id,
              order: item.order,
            })),
            ...overlaysPayload,
          };
          video = (await LiveStreamServiceInstance.updateVideo(id, data)).data;
        } else {
          const data: ICreateVideoRequest = {
            name: form.title,
            fadeBetweenBackgrounds: false,
            export: false,
            backgrounds: [
              {
                visualMediaId: form.backgroundId!,
                order: 0,
                startSecond: 0,
                endSecond: 20,
              },
            ],
            audios: selectedItems.map((item) => ({
              audioId: item.id,
              order: item.order,
            })),
            ...overlaysPayload,
          };
          video = (await LiveStreamServiceInstance.createVideo(id, data)).data;
          videoId = video.id;
        }

        if (!hideProFeatures && entitlement.features.scheduledShows && scheduledShows.length) {
          const newShowsVideos = scheduledShows.filter((show) =>
            show.id.startsWith("new")
          );
          const existingShowsVideos = scheduledShows.filter(
            (show) => !show.id.startsWith("new")
          );

          if (newShowsVideos.length) {
            const newShowsVideosData = (
              await LiveStreamServiceInstance.batchCreateVideo(
                id,
                newShowsVideos.map((show) => ({
                  name: show.name,
                  fadeBetweenBackgrounds: false,
                  export: false,
                  backgrounds: [
                    {
                      visualMediaId: show.backgroundId || form.backgroundId!,
                      order: 0,
                      startSecond: 0,
                      endSecond: 20,
                    },
                  ],
                  audios: show.items.map((item) => ({
                    audioId: parseShowItemId(item.id).itemId,
                    order: item.order,
                  })),
                  ...overlaysPayload,
                }))
              )
            ).data;
            newShowsVideosData.forEach((video) => {
              const show = scheduledShows.find(
                (show) => show.name === video.name
              );
              if (show) {
                liveStreamScheduledShows.push({
                  id: show.id,
                  name: show.name,
                  startMinuteOfDay: show.startMinuteOfDay,
                  timezone: show.timezone,
                  videoId: video.id,
                  switchMode: show.switchMode || "wait",
                });
              }
            });
          }
          if (existingShowsVideos.length) {
            const existingShowsVideosData = (
              await LiveStreamServiceInstance.batchUpdateVideos(
                id,
                existingShowsVideos.map((show) => ({
                  id: show.videoId!,
                  name: show.name,
                  fadeBetweenBackgrounds: false,
                  export: false,
                  backgrounds: [
                    {
                      visualMediaId: show.backgroundId || form.backgroundId!,
                      order: 0,
                      startSecond: 0,
                      endSecond: 20,
                    },
                  ],
                  audios: show.items.map((item) => ({
                    audioId: parseShowItemId(item.id).itemId,
                    order: item.order,
                  })),
                  ...overlaysPayload,
                }))
              )
            ).data;
            existingShowsVideosData.forEach((video) => {
              const show = scheduledShows.find(
                (show) => show.videoId === video.id
              );
              if (show) {
                liveStreamScheduledShows.push({
                  id: show.id,
                  name: show.name,
                  startMinuteOfDay: show.startMinuteOfDay,
                  timezone: show.timezone,
                  videoId: video.id,
                  switchMode: show.switchMode || "wait",
                });
              }
            });
          }
        }

        if (!hideProFeatures && entitlement.features.jingles) {
          if (jingleVideoId) {
            if (selectedJingleItems.length) {
              const jingleData: IUpdateVideoRequest = {
                id: jingleVideoId,
                name: `jingle_${Date.now()}`,
                fadeBetweenBackgrounds: false,
                export: false,
                backgrounds: [
                  {
                    visualMediaId: form.backgroundId!,
                    order: 0,
                    startSecond: 0,
                    endSecond: 20,
                  },
                ],
                audios: selectedJingleItems.map((item) => ({
                  audioId: parseJingleItemId(item.id),
                  order: item.order,
                })),
                ...overlaysPayload,
              };
              await LiveStreamServiceInstance.updateVideo(id, jingleData);
            } else {
              jingleVideoId = undefined;
            }
          } else if (selectedJingleItems.length) {
            const jingleData: ICreateVideoRequest = {
              name: `jingle_${Date.now()}`,
              fadeBetweenBackgrounds: false,
              export: false,
              backgrounds: [
                {
                  visualMediaId: form.backgroundId!,
                  order: 0,
                  startSecond: 0,
                  endSecond: 20,
                },
              ],
              audios: selectedJingleItems.map((item) => ({
                audioId: parseJingleItemId(item.id),
                order: item.order,
              })),
              ...overlaysPayload,
            };
            const jingle = await LiveStreamServiceInstance.createVideo(
              id,
              jingleData
            );
            jingleVideoId = jingle.data.id;
          }
        } else {
          jinglePlaylistId = undefined;
          jingleVideoId = undefined;
        }
      } else if (form.streamType === LiveStreamTypeEnum.playlist) {
        if (!hideProFeatures && entitlement.features.scheduledShows && scheduledShows.length) {
          const newShowsPlaylists = scheduledShows.filter((show) =>
            show.id.startsWith("new")
          );
          const existingShowsPlaylists = scheduledShows.filter(
            (show) => !show.id.startsWith("new")
          );

          if (newShowsPlaylists.length) {
            const newShowsPlaylistsData = (
              await LiveStreamServiceInstance.batchCreatePlaylist(
                id,
                newShowsPlaylists.map((show) => ({
                  name: show.name,
                  playlistVisualMedias: show.items.map((item) => ({
                    visualMediaId: parseShowItemId(item.id).itemId,
                    order: item.order,
                  })),
                  ...overlaysPayload,
                }))
              )
            ).data;
            newShowsPlaylistsData.forEach((playlist) => {
              const show = scheduledShows.find(
                (show) => show.name === playlist.name
              );
              if (show) {
                liveStreamScheduledShows.push({
                  id: show.id,
                  name: show.name,
                  startMinuteOfDay: show.startMinuteOfDay,
                  timezone: show.timezone,
                  playlistId: playlist.id,
                  switchMode: show.switchMode || "wait",
                });
              }
            });
          }
          if (existingShowsPlaylists.length) {
            const existingShowsPlaylistsData = (
              await LiveStreamServiceInstance.batchUpdatePlaylists(
                id,
                existingShowsPlaylists.map((show) => ({
                  id: show.playlistId!,
                  name: show.name,
                  playlistVisualMedias: show.items.map((item) => ({
                    visualMediaId: parseShowItemId(item.id).itemId,
                    order: item.order,
                  })),
                  ...overlaysPayload,
                }))
              )
            ).data;

            existingShowsPlaylistsData.forEach((playlist) => {
              const show = scheduledShows.find(
                (show) => show.playlistId === playlist.id
              );
              if (show) {
                liveStreamScheduledShows.push({
                  id: show.id,
                  name: show.name,
                  startMinuteOfDay: show.startMinuteOfDay,
                  timezone: show.timezone,
                  playlistId: playlist.id,
                  switchMode: show.switchMode || "wait",
                });
              }
            });
          }
        }

        if (!hideProFeatures && entitlement.features.jingles) {
          if (jinglePlaylistId) {
            if (selectedJingleItems.length) {
              const jingleData: IUpdatePlaylistRequest = {
                id: jinglePlaylistId,
                name: `jingle_${Date.now()}`,
                playlistVisualMedias: selectedJingleItems.map((item) => ({
                  visualMediaId: parseJingleItemId(item.id),
                  order: item.order,
                })),
                ...overlaysPayload,
              };
              await LiveStreamServiceInstance.updatePlaylist(id, jingleData);
            } else {
              jinglePlaylistId = undefined;
            }
          } else if (selectedJingleItems.length) {
            const jingleData: ICreatePlaylistRequest = {
              name: `jingle_${Date.now()}`,
              playlistVisualMedias: selectedJingleItems.map((item) => ({
                visualMediaId: parseJingleItemId(item.id),
                order: item.order,
              })),
              ...overlaysPayload,
            };
            const jingle = await LiveStreamServiceInstance.createPlaylist(
              id,
              jingleData
            );
            jinglePlaylistId = jingle.data.id;
          }
        } else {
          jinglePlaylistId = undefined;
          jingleVideoId = undefined;
        }
      }

      if (!hideProFeatures && entitlement.features.secondaryVideo) {
        if (secondaryVideoId) {
          if (selectedSecondaryItems.length) {
            const secondaryData: IUpdateVideoRequest = {
              id: secondaryVideoId,
              name: `secondary_${Date.now()}`,
              fadeBetweenBackgrounds: false,
              export: false,
              backgrounds: [
                {
                  visualMediaId: secondaryVideoVisualMediaId!,
                  order: 0,
                  startSecond: 0,
                  endSecond: 20,
                },
              ],
              audios: selectedSecondaryItems.map((item) => ({
                audioId: parseSecondaryItemId(item.id),
                order: item.order,
              })),
              ...overlaysPayload,
            };
            await LiveStreamServiceInstance.updateVideo(id, secondaryData);
          } else {
            secondaryVideoId = undefined;
          }
        } else if (selectedSecondaryItems.length) {
          const secondaryData: ICreateVideoRequest = {
            name: `secondary_${Date.now()}`,
            fadeBetweenBackgrounds: false,
            export: false,
            backgrounds: [
              {
                visualMediaId: secondaryVideoVisualMediaId!,
                order: 0,
                startSecond: 0,
                endSecond: 20,
              },
            ],
            audios: selectedSecondaryItems.map((item) => ({
              audioId: parseSecondaryItemId(item.id),
              order: item.order,
            })),
            ...overlaysPayload,
          };
          const secondary = await LiveStreamServiceInstance.createVideo(
            id,
            secondaryData
          );
          secondaryVideoId = secondary.data?.id;
        }
      } else {
        secondaryVideoId = undefined;
      }

      const liveStreamData = settingsBody(liveStreamScheduledShows);
      finalPutSent = true;
      const { data: saved } =
        await LiveStreamServiceInstance.updateLiveStream(liveStreamData);
      applyCommittedLiveStreamSave(
        id,
        saved,
        savedSnapshot,
        dispatch,
        getState as () => RootState
      );
      const awaitingExplicitStart =
        notificationMode === "goLive" &&
        saved.status === LiveStreamStatus.stopped &&
        !(saved.mediaApply?.action === "start" &&
          saved.mediaApply.state === "waiting");
      if (!awaitingExplicitStart) {
        const messageKey =
          notificationMode === "goLive"
            ? saved.mediaApply?.state === "waiting"
              ? saved.mediaApply.action === "start"
                ? "liveStream:notifications.startWaiting"
                : "liveStream:notifications.startedWithPending"
              : "liveStream:notifications.started"
            : saved.mediaApply?.state === "waiting"
              ? saved.mediaApply.action === "start"
                ? "liveStream:notifications.startWaiting"
                : "liveStream:notifications.updateWaiting"
              : saved.refresh === "deferred"
                ? "liveStream:notifications.updateDeferred"
                : "liveStream:notifications.updated";
        notification.success({ message: i18n.t(messageKey) });
      }
      await dispatch(refreshStreamSnapshotAfterMutation(id));
      return saved;
    } catch (err: any) {
      if (err?.message === "DRAFT_CONTENT_MISSING") {
        notification.error({
          message: i18n.t("liveStream:stepper.staleDraft.deletedTitle"),
        });
        throw err;
      }
      if (err?.message === "DRAFT_SERVER_CHANGED") {
        notification.error({
          message: i18n.t("liveStream:stepper.staleDraft.title"),
        });
        throw err;
      }
      // The content can disappear between preflight and a later content
      // write or the final settings PUT. Reconcile a 404 only when fresh
      // detail proves one of this draft's persisted targets is gone.
      // Leave edited form and media selections intact for explicit Reload.
      if (errorStatusOf(err) === 404 && current?.id === id) {
        try {
          const { data: fresh } =
            await LiveStreamServiceInstance.getLiveStreamById(id);
          const expected = draft?.basePersistedTargets ?? persistedContentTargets(current);
          const targetStatus = draftTargetStatus(expected, fresh, scheduledShows);
          if (targetStatus !== "same") {
            if ((getState() as RootState).liveStream.liveStreamById?.id === id) {
              dispatch(setLiveStreamById(fresh));
            }
            if (targetStatus === "deleted") {
              dispatch(flagMissingPersistedContent(id));
              notification.error({
                message: i18n.t("liveStream:stepper.staleDraft.deletedTitle"),
              });
              throw new Error("DRAFT_CONTENT_MISSING");
            }
            dispatch(flagStaleLiveStreamWizardDraft({
              streamId: id,
              serverRevision: fresh.contentRevision ?? draft?.baseContentRevision ?? 0,
              serverFingerprint: draftSourceFingerprint(fresh),
            }));
          }
        } catch (refreshErr: any) {
          if (refreshErr?.message === "DRAFT_CONTENT_MISSING") throw refreshErr;
          // The detail itself may have gone; keep the original API error.
        }
      }
      // Task 21 (B4 x B5): update() commits the settings before its
      // post-save `switch` runs. A refusal from the final PUT therefore means
      // "saved, but the follow-up action was refused" — the draft is clean,
      // but there is no `saved` body to rebase from (the request errored), so
      // re-fetch the stream to pick up the server ids the commit produced.
      if (finalPutSent && isPostCommitRefusal(err)) {
        try {
          const { data: refetched } =
            await LiveStreamServiceInstance.getLiveStreamById(id);
          // Review fix round 3 (Important 1): don't let A's own re-fetch
          // replace `liveStreamById` while B's detail page is on screen.
          if ((getState() as RootState).liveStream.liveStreamByIdRequestedId === id) {
            dispatch(setLiveStreamById(refetched));
          }
          applyCommittedLiveStreamSave(
            id,
            refetched,
            savedSnapshot,
            dispatch,
            getState as () => RootState
          );
        } catch (refetchErr) {
          // The commit still happened even if this re-fetch failed; at least
          // don't leave the draft stuck dirty over a save that succeeded. Log
          // it: the draft keeps its "new…" show placeholders until the next
          // successful fetch, so a silent failure here can duplicate shows.
          console.error(
            "updateLiveStream: post-commit re-fetch failed after a refused start",
            refetchErr
          );
          (dispatch as any)(markLiveStreamWizardDraftSaved(id, savedSnapshot));
        }
      }
      dispatchErrorNotification(err);
      throw err;
    } finally {
      dispatch(setUpdateLiveStreamLoading(false));
    }
  }
);

export const updateVideoKey = createAsyncThunk(
  "liveStream/updateVideoKey",
  async (
    {
      id,
      platformId,
      videoKey,
    }: { id: string; platformId: string; videoKey: string },
    { dispatch }
  ) => {
    try {
      await LiveStreamServiceInstance.updateVideoKey({
        id,
        platformId,
        videoKey,
      });
      await dispatch(refreshStreamSnapshotAfterMutation(id));
      notification.success({
        message: i18n.t("liveStream:notifications.analyticsConnected"),
      });
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
  }
);

export const updatePromoted = createAsyncThunk(
  "liveStream/updatePromoted",
  async (
    {
      id,
      promoted,
      promotedUrl,
    }: { id: string; promoted: boolean; promotedUrl: string },
    { dispatch }
  ) => {
    try {
      await LiveStreamServiceInstance.updatePromoted({
        id,
        promoted,
        promotedUrl,
      });
      await dispatch(refreshStreamSnapshotAfterMutation(id));
      notification.success({
        message: i18n.t("liveStream:notifications.updateGeneric"),
      });
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
  }
);

export const restartLiveStream = createAsyncThunk(
  "liveStream/restartLiveStream",
  async (id: string, { dispatch }) => {
    try {
      await LiveStreamServiceInstance.restartLiveStream(id);
      await dispatch(refreshStreamSnapshotAfterMutation(id));
      notification.success({
        message: i18n.t("liveStream:notifications.restarted"),
      });
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
  }
);

export const playLiveStreamItem = createAsyncThunk(
  "liveStream/playLiveStreamItem",
  async (
    { streamId, itemId }: { streamId: string; itemId: string },
    { dispatch }
  ) => {
    try {
      await LiveStreamServiceInstance.playLiveStreamItem(streamId, itemId);
      await dispatch(refreshStreamSnapshotAfterMutation(streamId));
      notification.success({
        // Part C #5: Rust drops a jump while a jingle or show plays and never
        // says so, so the api cannot confirm the jump happened.
        message: i18n.t("liveStream:notifications.jumpRequested"),
      });
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
  }
);

export const endLiveStream = createAsyncThunk(
  "liveStream/endLiveStream",
  async (id: string, { dispatch }) => {
    try {
      await LiveStreamServiceInstance.endLiveStream(id);
      await dispatch(refreshStreamSnapshotAfterMutation(id));
      notification.success({
        message: i18n.t("liveStream:notifications.ended"),
      });
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
  }
);

export const stopLiveStream = createAsyncThunk(
  "liveStream/stopLiveStream",
  async (id: string, { dispatch }) => {
    try {
      await LiveStreamServiceInstance.stopLiveStream(id);
      await dispatch(refreshStreamSnapshotAfterMutation(id));
      notification.success({
        message:
          "Stopping your live stream! You can update & start it again at any time.",
      });
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
  }
);

export const startLiveStream = createAsyncThunk(
  "liveStream/startLiveStream",
  async (id: string, { dispatch }) => {
    dispatch(setUpdateLiveStreamLoading(true));
    try {
      // The api dedupes a repeated Start by status; it returns {status, streamId}.
      const { data: started } = await LiveStreamServiceInstance.startLiveStream(id);
      await dispatch(refreshStreamSnapshotAfterMutation(id));
      notification.success({
        message: i18n.t(
          started?.mediaApply?.state === "waiting"
            ? started.mediaApply.action === "start"
              ? "liveStream:notifications.startWaiting"
              : "liveStream:notifications.startedWithPending"
            : "liveStream:notifications.started"
        ),
      });
    } catch (err: any) {
      dispatchErrorNotification(err);
      throw err;
    } finally {
      dispatch(setUpdateLiveStreamLoading(false));
    }
  }
);
